import { useLocation, useNavigate } from "react-router-dom";
import { useCentralStore } from "@/dentlab/src/store/Central";
import {
	MessageCircle,
	FileText,
	ExternalLink,
	Settings,
	Lightbulb,
	Archive,
} from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { RolesEnum } from "@/lib/types/roles.types";

export const ClientMenu = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const currentPath = location.pathname;

	const { client, role } = useCentralStore((state) => ({
		client: state.client,
		role: state.role ?? 2,
	}));

	const baseUrl = `/client/${client?.id}`;

	// Check if the current path includes a specific path segment
	const isPathActive = (path: string) => {
		// Special case for debitoren which is not under client path
		if (path === "debitoren") {
			return currentPath.includes("/debitoren");
		}
		return currentPath.includes(`${baseUrl}/${path}`);
	};

	const navigateTo = (path: string) => {
		if (!isPathActive(path)) {
			if (path.includes("debitoren")) {
				navigate(`/debitoren/fakturajournal?recipient=${client?.id}`);
				return;
			}
			navigate(`${baseUrl}/${path}`);
		}
	};

	const isConnected = !!client?.connected_organization_id;

	const menuItems = [
		...(isConnected
			? [
					{
						title: "Auftragschats",
						icon: <MessageCircle size={16} />,
						onClick: () => navigateTo(`chats`),
						path: "chats",
					},
					{
						title: "Allgemeiner Chat",
						icon: <MessageCircle size={16} />,
						onClick: () => navigateTo(`general-chat`),
						path: "general-chat",
					},
				]
			: []),
		/* 		{
			title: "Patienten",
			icon: <Users size={16} />,
			onClick: () => navigateTo("patienten"),
			path: "patienten",
		}, */
		...(role <= RolesEnum.ADMIN
			? [
					{
						title: "Rechnungen",
						icon: <FileText size={16} />,
						onClick: () =>
							navigateTo(`debitoren?recipient=${client?.id}`),
						path: "debitoren",
						hasExternalIndicator: true,
					},
					{
						title: "Kundeneinstellungen",
						icon: <Settings size={16} />,
						onClick: () => navigateTo(`einstellungen`),
						path: "einstellungen",
					},
				]
			: []),
		{
			title: "Archiv",
			icon: <Archive size={16} />,
			onClick: () => navigateTo(`dateiarchiv`),
			path: "dateiarchiv",
		},
	];

	return (
		<div className="flex items-center gap-1">
			{menuItems.map((item, index) => (
				<button
					key={index}
					onClick={item.onClick}
					className={cn(
						"flex items-center gap-1 px-2 py-1 text-sm rounded-md transition-colors",
						isPathActive(item.path)
							? "bg-stone-200 text-gray-800 font-medium"
							: "text-gray-700 hover:bg-stone-200"
					)}
					aria-label={item.title}
					data-testid={`client-menu-${item.path}`}
				>
					<span
						className={
							isPathActive(item.path)
								? "text-gray-800"
								: "text-gray-600"
						}
					>
						{item.icon}
					</span>
					<span>{item.title}</span>
					{item.hasExternalIndicator && (
						<ExternalLink
							size={14}
							className="ml-0.5 text-gray-500"
						/>
					)}
				</button>
			))}
			{!isConnected && (
				<button
					onClick={() => navigateTo(`einstellungen/verbindung`)}
					className="flex items-center gap-1 px-2 py-1 text-sm rounded-md hover:bg-stone-200"
					aria-label="Mit dentCONNECT verbinden"
					data-testid="dentconnect-warning-banner"
				>
					<Lightbulb size={16} className="text-gray-600" />
					<span>Mit dentCONNECT verbinden</span>
					<ExternalLink size={14} className="ml-0.5 text-gray-500" />
				</button>
			)}
		</div>
	);
};
